import { tt } from '@agos/shared'

/**
 * *Platby zákazníkov* on *Nastavenia rozvozu* - FRONTEND.md §9.766, owner O127, on BACKEND.md §10A.418.
 *
 * The owner: *"Can you add buttons like... where I can for customer add specifically QR Platba selection, or
 * specifically disable Karta - Terminál?"* - and, asked back, here, in a section of its own, with *Pravidlá
 * pokladne* moved in from *Platby online - archív*.
 *
 * **Read from the public answer for everybody.** `GET /settings/payments/public` is what the checkout reads, and since
 * §10A.418 it answers all four switches and whether a QR can be honoured now - so the screen shows what the customer
 * is offered, from the same read, for a main administrator and a waiter alike. `GET /settings/payments` is the main
 * administrator's (§9.750), and reading it for one viewer and the public answer for another is how two people looked
 * at one screen and saw two things.
 */

/** The four switches, and whether a QR can be honoured now. */
export interface CustomerPayments {
  /** *Karta ako predvolená platba* - card online starts selected. */
  defaultOnline: boolean
  /** *Karta aj pri vyzdvihnutí* - card online offered on a pickup too. */
  allowOnlinePickup: boolean
  /** *Ponúkať Kartu - terminál*. */
  offerCardTerminal: boolean
  /** *Ponúkať QR platbu*, as stored. */
  offerQrPay: boolean
  /** Offered **and** the driver's QR would work now - the server's `qrPayAvailable`. */
  qrPayAvailable: boolean
}

/** The keys of the four switches, in the order they are drawn and sent - `PaymentSettings.CHECKOUT_PREFERENCES`. */
export const CUSTOMER_PAYMENT_SWITCHES = [
  'defaultOnline',
  'allowOnlinePickup',
  'offerCardTerminal',
  'offerQrPay',
] as const

export type CustomerPaymentSwitch = (typeof CUSTOMER_PAYMENT_SWITCHES)[number]

/**
 * Read the public answer. **Null unless all five are booleans** - a server older than §10A.418 answers three, and a
 * switch it did not answer drawn as *off* is the false claim §9.750 removed from the page this moved out of.
 */
export function customerPaymentsOf(payload: unknown): CustomerPayments | null {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null
  const row = payload as Record<string, unknown>
  const {
    defaultOnline,
    allowOnlinePickup,
    offerCardTerminal,
    offerQrPay,
    qrPayAvailable,
  } = row
  if (
    typeof defaultOnline !== 'boolean' ||
    typeof allowOnlinePickup !== 'boolean' ||
    typeof offerCardTerminal !== 'boolean' ||
    typeof offerQrPay !== 'boolean' ||
    typeof qrPayAvailable !== 'boolean'
  ) {
    return null
  }
  return { defaultOnline, allowOnlinePickup, offerCardTerminal, offerQrPay, qrPayAvailable }
}

/**
 * What a press of one switch sends: **all four**, with the pressed one flipped. The whole form, as every settings
 * screen here sends it; `FieldRights` compares by value, so the three unchanged ones are not changes (§10A.413).
 */
export function customerPaymentsPayload(
  current: CustomerPayments,
  flipped: CustomerPaymentSwitch,
): Record<CustomerPaymentSwitch, boolean> {
  return {
    defaultOnline: flipped === 'defaultOnline' ? !current.defaultOnline : current.defaultOnline,
    allowOnlinePickup: flipped === 'allowOnlinePickup' ? !current.allowOnlinePickup : current.allowOnlinePickup,
    offerCardTerminal: flipped === 'offerCardTerminal' ? !current.offerCardTerminal : current.offerCardTerminal,
    offerQrPay: flipped === 'offerQrPay' ? !current.offerQrPay : current.offerQrPay,
  }
}

/**
 * Whether QR is switched on for customers and cannot be honoured - so customers are **not** offered it, and the
 * person who switched it on has to be told why. The cause is on this same screen: the *QR platba* tab (switched off,
 * no account in bank mode, or a gateway that is not set up in gateway mode, §10A.333).
 */
export function qrOfferedButUnusable(payments: CustomerPayments): boolean {
  return payments.offerQrPay && !payments.qrPayAvailable
}

/**
 * The right `PUT /settings/payments` asks for the four - `OnlinePaymentController.updateSettings`'
 * `@RequirePermission("online_payments")`, which the main administrator passes before anything is read. Spelled here
 * rather than imported, because §5 keeps a module from importing another; `manager-settings`' `write-access.ts`
 * spells it as `PERMISSION_WRITABLE.payments`, and a backend test holds both to the Java.
 */
export const CUSTOMER_PAYMENTS_RIGHT = 'online_payments'

export function mayEditCustomerPayments(
  systemRole: string | undefined,
  hasPermission: (permission: string) => boolean,
): boolean {
  if (systemRole === 'main_admin') return true
  return hasPermission(CUSTOMER_PAYMENTS_RIGHT)
}

/** Said under the switches to whoever may not change them, naming the right as the roles screen labels it. */
export function customerPaymentsReadOnlyNote(): string {
  return `${tt('Tieto nastavenia mení len ten, kto má právo')} „${tt('Platby online')}“.`
}
